import { useEffect, useRef, useState } from "react";
import { Play, Pause, Film } from "lucide-react";
import { useSignedFiles } from "@/hooks/useSignedFiles";

// Lightweight session replay: plays stored low-res JPEG frames back as a timelapse.
export function ReplayPlayer({ frames = [], apiBase, token, fps = 4, seekTo, seekNonce }) {
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const timer = useRef(null);
  const signUrl = useSignedFiles(frames.map((f) => f.path));

  useEffect(() => {
    if (seekNonce === undefined) return;
    setPlaying(false);
    setIdx(Math.max(0, Math.min(seekTo ?? 0, frames.length - 1)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seekNonce]);

  useEffect(() => {
    if (!playing) return undefined;
    timer.current = setInterval(() => {
      setIdx((i) => {
        if (i >= frames.length - 1) {
          setPlaying(false);
          return i;
        }
        return i + 1;
      });
    }, 1000 / fps);
    return () => clearInterval(timer.current);
  }, [playing, frames.length, fps]);

  if (!frames.length) return null;
  const current = frames[Math.min(idx, frames.length - 1)];
  const src = signUrl(current.path);

  return (
    <div className="space-y-2" data-testid="proctor-replay">
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
          <Film className="h-3.5 w-3.5" /> Session replay
        </span>
        <span className="text-[10px] font-mono text-muted-foreground">
          {current.at ? new Date(current.at).toLocaleTimeString() : ""} · {idx + 1}/{frames.length}
        </span>
      </div>
      <div className="relative aspect-video rounded-xl overflow-hidden border border-white/10 bg-black">
        <img src={src} alt="session frame" className="h-full w-full object-cover" data-testid="replay-frame" />
        <button
          onClick={() => {
            if (idx >= frames.length - 1) setIdx(0);
            setPlaying((p) => !p);
          }}
          className="absolute inset-0 flex items-center justify-center bg-black/0 hover:bg-black/25 transition-colors"
          data-testid="replay-toggle"
          aria-label={playing ? "Pause replay" : "Play replay"}
        >
          <span className={`h-11 w-11 rounded-full bg-black/60 backdrop-blur flex items-center justify-center border border-white/20 transition-opacity ${playing ? "opacity-0 hover:opacity-100" : "opacity-100"}`}>
            {playing ? <Pause className="h-5 w-5 text-white" /> : <Play className="h-5 w-5 text-white ml-0.5" />}
          </span>
        </button>
      </div>
      <input
        type="range"
        min={0}
        max={frames.length - 1}
        value={Math.min(idx, frames.length - 1)}
        onChange={(e) => { setPlaying(false); setIdx(Number(e.target.value)); }}
        className="w-full accent-amber-500 h-1.5 cursor-pointer"
        data-testid="replay-scrubber"
      />
    </div>
  );
}
